import React, { useState } from 'react';
import axios from 'axios';
import EmailComposer from '../components/EmailComposer';

const defaultBlock = { type: 'richtext', value: '', align: 'left' };

const EmailTest = () => {
  const [testEmailFields, setTestEmailFields] = useState({
    subject: '',
    previewText: '',
    to: [],
    cc: [],
    bcc: []
  });
  const [bodyBlocks, setBodyBlocks] = useState([{ ...defaultBlock }]);
  const [testResult, setTestResult] = useState(null);
  const [loading, setLoading] = useState(false);

  // Compose HTML from blocks (for test email)
  const buildBodyHtml = () => {
    return bodyBlocks.map((block, idx) => {
      const align = block.align || 'left';
      let alignStyle = '';
      if (align === 'center') alignStyle = 'text-align:center;';
      if (align === 'right') alignStyle = 'text-align:right;';
      switch (block.type) {
        case 'richtext':
          return `<div style="${alignStyle}">${block.value || ''}</div>`;
        case 'button':
          if (!block.value.text || !block.value.link) return '';
          let btnWrap = align === 'center' ? 'margin:16px auto;text-align:center;display:block;' : align === 'right' ? 'margin:16px 0 16px auto;text-align:right;display:block;' : 'margin:16px 0;text-align:left;display:block;';
          return `<div style="${btnWrap}"><a href="${block.value.link}" style="background:#2563eb;color:#fff;padding:12px 24px;border-radius:6px;text-decoration:none;display:inline-block;font-weight:600;">${block.value.text}</a></div>`;
        case 'image':
          if (!block.value.src) return '';
          const w = block.value.width ? `width:${block.value.width}px;` : '';
          const h = block.value.height ? `height:${block.value.height}px;` : '';
          let imgWrap = align === 'center' ? 'text-align:center;' : align === 'right' ? 'text-align:right;' : 'text-align:left;';
          return `<div style="margin:16px 0;${imgWrap}"><img src="${block.value.src}" style="max-width:100%;${w}${h}" alt="Image" /></div>`;
        default:
          return '';
      }
    }).join('');
  };

  // Test email handler
  const handleTestEmail = async (e) => {
    e.preventDefault();
    setTestResult(null);
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const html = buildBodyHtml();
      const response = await axios.post('/api/emails/test', {
        to: testEmailFields.to.filter(Boolean),
        cc: testEmailFields.cc.filter(Boolean),
        bcc: testEmailFields.bcc.filter(Boolean),
        subject: testEmailFields.subject,
        previewText: testEmailFields.previewText,
        html
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTestResult(response.data);
    } catch (error) {
      setTestResult({
        success: false,
        error: error.response?.data?.error || 'Failed to send test email'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f8fafc 0%, #e0e7ff 100%)', padding: '48px 0', fontFamily: 'inherit' }}>
      {/* Modern Header */}
      <div style={{
        textAlign: 'center',
        marginBottom: 48,
        padding: '40px 24px',
        background: 'linear-gradient(135deg, #2563eb 0%, #60a5fa 100%)',
        borderRadius: 24,
        color: 'white',
        boxShadow: '0 10px 30px rgba(37,99,235,0.18)',
        position: 'relative',
        overflow: 'hidden',
        maxWidth: 900,
        marginLeft: 'auto',
        marginRight: 'auto'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'url("data:image/svg+xml,%3Csvg width=\"60\" height=\"60\" viewBox=\"0 0 60 60\" xmlns=\"http://www.w3.org/2000/svg\"%3E%3Cg fill=\"none\" fill-rule=\"evenodd\"%3E%3Cg fill=\"%23ffffff\" fill-opacity=\"0.05\"%3E%3Ccircle cx=\"30\" cy=\"30\" r=\"2\"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          opacity: 0.3
        }} />
        <h1 style={{
          marginBottom: 8,
          fontWeight: 800,
          letterSpacing: 1.5,
          fontSize: 36,
          position: 'relative',
          zIndex: 1
        }}>
          Email Testing & Management
        </h1>
        <div style={{
          fontStyle: 'italic',
          fontSize: 20,
          opacity: 0.92,
          fontWeight: 500,
          position: 'relative',
          zIndex: 1
        }}>
          Send, preview, and manage emails with a modern UI
        </div>
      </div>

      {/* Single Email Card */}
      <div style={{ maxWidth: 800, margin: '0 auto', background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)', borderRadius: 24, boxShadow: '0 8px 32px rgba(0,0,0,0.08)', padding: 40, border: '1px solid rgba(102, 126, 234, 0.08)', marginBottom: 40 }}>
        {/* Section Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
          <div style={{ width: 6, height: 32, background: 'linear-gradient(135deg, #2563eb 0%, #60a5fa 100%)', borderRadius: 3 }} />
          <div style={{ fontSize: 26, fontWeight: 800, color: '#2563eb', letterSpacing: 1 }}>Test Single Email</div>
          <span style={{ fontSize: 15, color: '#888', fontWeight: 500, marginLeft: 8, fontStyle: 'italic' }} title="This tab allows you to test sending a single email to one or more recipients with advanced customization.">?</span>
        </div>

        <form onSubmit={handleTestEmail}>
          <EmailComposer
            emailFields={testEmailFields}
            onEmailFieldsChange={setTestEmailFields}
            bodyBlocks={bodyBlocks}
            onBodyBlocksChange={setBodyBlocks}
            showAttributeInserter={true}
          >
            {/* Send Test Email Button */}
            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                background: loading ? 'linear-gradient(135deg, #a5b4fc 0%, #818cf8 100%)' : 'linear-gradient(135deg, #2563eb 0%, #60a5fa 100%)',
                color: 'white',
                padding: '18px 0',
                border: 'none',
                borderRadius: 16,
                fontWeight: 800,
                fontSize: 20,
                cursor: loading ? 'not-allowed' : 'pointer',
                letterSpacing: 1,
                boxShadow: loading ? 'none' : '0 8px 25px rgba(37,99,235,0.18)',
                transition: 'all 0.3s',
                marginTop: 8
              }}
            >
              {loading ? (
                <span style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center', animation: 'pulse 1.2s infinite' }}>⬆️ Sending...</span>
              ) : (
                <span style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}>⬆️ Send Test Email</span>
              )}
            </button>
          </EmailComposer>
        </form>

        {/* Test Result */}
        {testResult && (
          <div style={{ marginTop: 32, padding: 24, borderRadius: 16, boxShadow: '0 2px 8px rgba(60,72,100,0.07)', background: testResult.success ? 'linear-gradient(135deg, #d1fae5 0%, #bbf7d0 100%)' : 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)', color: testResult.success ? '#065f46' : '#b91c1c', fontWeight: 700, fontSize: 18, display: 'flex', alignItems: 'center', gap: 12, animation: testResult.success ? 'pulse 1.2s 2' : 'shake 0.5s 2' }}>
            <span style={{ fontSize: 28 }}>{testResult.success ? '✅' : '❌'}</span>
            <span>{testResult.success ? 'Success!' : 'Error'}</span>
            <span style={{ fontWeight: 500, fontSize: 15, color: '#444', marginLeft: 12 }}>{testResult.message || testResult.error}</span>
            {testResult.messageId && (
              <span style={{ fontSize: 13, color: '#666', marginLeft: 18 }}>Message ID: {testResult.messageId}</span>
            )}
          </div>
        )}
      </div>

      {/* Keyframes for animations */}
      <style>{`
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        @keyframes pulse {
          0% { opacity: 1; }
          50% { opacity: 0.6; }
          100% { opacity: 1; }
        }
        @keyframes shake {
          0% { transform: translateX(0); }
          20% { transform: translateX(-8px); }
          40% { transform: translateX(8px); }
          60% { transform: translateX(-8px); }
          80% { transform: translateX(8px); }
          100% { transform: translateX(0); }
        }
      `}</style>
    </div>
  );
};

export default EmailTest; 